import './style.scss'
import {
	Button,
	FormControl,
	Grid2,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Box,
	Typography,
	Card,
	CardContent,
	Stack,
	Chip,
	IconButton,
	Tooltip,
	Divider,
	Alert,
	CircularProgress
} from '@mui/material'
import { getUsers } from '../../services/user.js'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { format, formatDate } from 'date-fns'
import { getSadhna } from '../../services/sadhna.js'
import { GridSearchIcon } from '@mui/x-data-grid'
import { getLast15thDate, SadhnaHelper } from './helper.js'
import { HIDE_MAIN_LOADER, SHOW_MAIN_LOADER } from '../../constants/actions.js'
import store from '../../store.js'
import { SADHNA_GROUP, USER_ROLE } from '../../constants/index.js'
import CustomizedDataGrid from '../AppDashboard/components/CustomizedDataGrid.js'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
	DownloadForOffline,
	Search as SearchIcon,
	FilterList as FilterIcon,
	Assessment as AssessmentIcon,
	CalendarToday as CalendarIcon,
	People as PeopleIcon
} from '@mui/icons-material'
import moment from 'moment'
var FileSaver = require('file-saver')
const ExcelJS = require('exceljs')

export function valueFormatter(value) {
	return `${value}%`
}

const generateColumns = (data) => {
	const columns = [
		{
			field: 'srNo',
			headerName: 'Sr No',
			width: 100,
			renderCell: (params) => (
				<Chip
					label={`#${params.value}`}
					size="small"
					variant="outlined"
					sx={{
						fontWeight: 600,
						minWidth: '50px',
						height: '24px',
						fontSize: '0.75rem',
						borderColor: params.value <= 3 ? 'gold' : params.value <= 10 ? 'silver' : 'inherit',
						color: params.value <= 3 ? 'gold' : params.value <= 10 ? 'silver' : 'inherit',
					}}
				/>
			)
		},
		{
			field: 'userName',
			headerName: 'User Name',
			width: 200,
			renderCell: (params) => (
				<Box sx={{ fontWeight: 600, color: 'text.primary' }}>
					{params.value}
				</Box>
			)
		},
		{
			field: 'connectedAreaName',
			headerName: 'Connected Area',
			width: 200,
			renderCell: (params) => (
				<Chip
					label={params.value || 'N/A'}
					size="small"
					variant="outlined"
					sx={{ fontSize: '0.75rem', height: '20px' }}
				/>
			)
		},
		{
			field: 'dayCount',
			headerName: 'Day Count',
			width: 150,
			renderCell: (params) => (
				<Chip
					label={params.value}
					size="small"
					color="primary"
					variant="filled"
					sx={{ fontWeight: 600, minWidth: '50px', height: '24px' }}
				/>
			)
		},
	]

	const rows = data.map((item, index) => {
		// Calculate averages for compiledSadhna
		const totalSadhna = item.compiledSadhna.length
		const averageSadhna = item.compiledSadhna.reduce((acc, day) => {
			Object.keys(day).forEach((key) => {
				acc[key] = (acc[key] || 0) + parseInt(day[key] || 0)
			})
			return acc
		}, {})

		Object.keys(averageSadhna).forEach((key) => {
			averageSadhna[key] = (averageSadhna[key] / totalSadhna).toFixed(2)
		})

		const totalTypeOfSadhna = Object.keys(averageSadhna).length

		const overallAverage = (
			Object.values(averageSadhna).reduce(
				(acc, val) => acc + parseInt(val || 0),
				0
			) / totalTypeOfSadhna
		)?.toFixed?.(2)

		const overallPercentage = (
			(parseFloat(overallAverage) * 100) /
			25
		)?.toFixed?.(2)

		averageSadhna.overallAverage = isNaN(overallAverage)
			? ''
			: overallAverage

		averageSadhna.overallPercentage = isNaN(overallPercentage)
			? ''
			: `${overallPercentage} %`

		// Create row object
		return {
			id: item._id || index,
			srNo: index + 1,
			userName: item.name || 'N/A',
			dayCount: totalSadhna,
			connectedAreaName: item?.connectedAreaId?.name,
			...averageSadhna,
		}
	})

	// Add dynamic columns for each sadhna
	const sadhnaWithScore = data.find((el) => el.compiledSadhna.length > 0)
	if (sadhnaWithScore && sadhnaWithScore?.compiledSadhna?.length > 0) {
		const sampleSadhna = sadhnaWithScore.compiledSadhna[0]
		Object.keys(sampleSadhna).forEach((key) => {
			columns.push({
				field: key,
				headerName: key
					.replace(/([A-Z])/g, ' $1')
					.replace(/^./, (str) => str.toUpperCase()), // Format header
				width: 150,
				renderCell: (params) => (
					<Chip
						label={params.value}
						size="small"
						variant="outlined"
						sx={{
							fontSize: '0.75rem',
							height: '20px',
							borderColor: parseFloat(params.value) >= 20 ? 'success.main' :
								parseFloat(params.value) >= 15 ? 'warning.main' : 'error.main',
							color: parseFloat(params.value) >= 20 ? 'success.main' :
								parseFloat(params.value) >= 15 ? 'warning.main' : 'error.main'
						}}
					/>
				)
			})
		})
	}
	columns.push(
		{
			field: 'overallAverage',
			headerName: 'Overall Average',
			width: 150,
			renderCell: (params) => (
				<Chip
					label={params.value}
					size="small"
					color="primary"
					variant="filled"
					sx={{ fontWeight: 600, minWidth: '60px', height: '24px' }}
				/>
			)
		},

		{
			field: 'overallPercentage',
			headerName: 'Overall Percentage',
			width: 150,
			renderCell: (params) => {
				const percentage = parseFloat(params.value)
				const color = percentage >= 80 ? 'success' : percentage >= 60 ? 'warning' : 'error'
				return (
					<Chip
						label={params.value}
						size="small"
						color={color}
						variant="filled"
						sx={{ fontWeight: 600, minWidth: '70px', height: '24px' }}
					/>
				)
			}
		}
	)

	return { columns, rows }
}

const downloadReport = ({ rows, columns, fileName = 'SadhnaReport.xlsx' }) => {
	const workbook = new ExcelJS.Workbook()
	const worksheet = workbook.addWorksheet('Sadhna Report')

	// Define worksheet columns
	worksheet.columns = columns.map((col) => ({
		header: col.headerName, // Set the header name
		key: col.field, // Map the field name to the key
		width: col.width / 10, // Optional: Set width (adjust scale if needed)
	}))

	// Add data rows
	rows.forEach((row) => {
		const rowData = columns.map((col) => row[col.field])
		worksheet.addRow(rowData)
	})

	// Generate Excel file and trigger download
	workbook.xlsx.writeBuffer().then((buffer) => {
		FileSaver.saveAs(new Blob([buffer]), fileName)
	})
}

const SadhnaReportContainer = () => {
	const [filter, setFilter] = useState({
		category: 4,
		user: null,
		from: getLast15thDate(),
		to: new Date(),
		areaId: '',
	})

	const loaderShownRef = useRef(false)

	console.log(filter.category, 'filter.category')
	// Fetch users based on the filter
	const { data: users = [], isLoading: usersLoading } = useQuery({
		queryKey: ['users', String(filter.category)],
		queryFn: async () => {
			console.log('calling users', filter.category)
			const isNumericCategory = !isNaN(filter.category) && !isNaN(parseFloat(filter.category)) && filter.category !== ''
			const request = isNumericCategory
				? { userRole: parseInt(filter.category) }
				: { sadhnaGroup: SADHNA_GROUP[filter.category].id }
			const users = await getUsers({
				...request,
				per_page: 1000,
				select: '_id name displayName',
				isShort: true,
			}).then((res) => res?.data?.data || [])

			console.log(users, 'users')

			return users
		},
		enabled: !!filter.category
	})



	// Combined query to fetch users with their sadhna data
	const { data: usersWithSadhnaData = [], isLoading: isSadhnaLoading } = useQuery({
		queryKey: ['usersWithSadhna', filter.category, filter.from, filter.to],
		queryFn: async () => {
			if (!users.length || !filter.from || !filter.to) return []

			// Fetch sadhna data for all users in parallel
			const sadhnaPromises = users.map(user =>
				getSadhna({
					from: format(new Date(filter.from), 'yyyy-MM-dd'),
					to: format(new Date(filter.to), 'yyyy-MM-dd'),
					userId: user._id,
					perPage: 365,
				}).then(res => res?.data?.data || [])
			)

			const sadhnaResults = await Promise.all(sadhnaPromises)

			// Transform and combine the data
			return users.map((user, index) => ({
				...user,
				id: user._id,
				sadhnaData: sadhnaResults[index] || [],
				compiledSadhna: (sadhnaResults[index] || []).map((sadhna) =>
					SadhnaHelper.getIndividualActivityMarks(sadhna)
				),
			}))
		},
		enabled: !!users.length && !!filter.from && !!filter.to,
	})

	const isLoading = usersLoading || isSadhnaLoading

	useEffect(() => {
		// Show loader when any query is loading
		if (isLoading) {
			if (!loaderShownRef.current) {
				store.dispatch({ type: SHOW_MAIN_LOADER })
				loaderShownRef.current = true
			}
		} else {
			// Only hide loader when all queries are complete
			if (loaderShownRef.current) {
				store.dispatch({ type: HIDE_MAIN_LOADER })
				loaderShownRef.current = false
			}
		}
	}, [isLoading])

	// Cleanup loader on component unmount
	useEffect(() => {
		return () => {
			if (loaderShownRef.current) {
				store.dispatch({ type: HIDE_MAIN_LOADER })
				loaderShownRef.current = false
			}
		}
	}, [])

	const onChangeFilter = (key, value) => {
		setFilter((prevData) => ({
			...prevData,
			[key]: value,
		}))
	}

	const { rows, columns } = useMemo(
		() => generateColumns(usersWithSadhnaData),
		[usersWithSadhnaData]
	)

	const onRowClick = () => { }

	return (
		<Box sx={{
			minHeight: '100vh',
			maxWidth: '100%',
			padding: { xs: 2, sm: 3, md: 4 }
		}}>
			{/* Header Section */}
			<Card sx={{
				mb: 3,
				background: 'rgba(255, 255, 255, 0.95)',
				backdropFilter: 'blur(10px)',
				borderRadius: '20px',
				boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
			}}>
				<CardContent sx={{ p: 3 }}>
					<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
						<AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
						<Box>
							<Typography variant="h4" component="h1" sx={{
								fontWeight: 700,
								background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
								backgroundClip: 'text',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent'
							}}>
								Sadhna Report Dashboard
							</Typography>
							<Typography variant="body1" color="text.secondary">
								Comprehensive spiritual progress tracking and analytics
							</Typography>
						</Box>
					</Stack>

					{/* Filters Section */}
					<Box sx={{
						display: 'flex',
						flexDirection: { xs: 'column', md: 'row' },
						gap: 2,
						alignItems: { xs: 'stretch', md: 'center' }
					}}>
						<FormControl
							variant="outlined"
							size="small"
							sx={{ minWidth: { xs: '100%', md: 200 } }}
						>
							<InputLabel sx={{ color: 'text.secondary' }}>Category</InputLabel>
							<Select
								value={filter.category}
								onChange={(e) => onChangeFilter('category', e.target.value)}
								label="Category"
								sx={{
									borderRadius: '12px',
									'& .MuiSelect-select': {
										padding: '12px 16px'
									}
								}}
							>
								{[
									...Object.entries(USER_ROLE),
									...Object.entries(SADHNA_GROUP)
										.filter(
											(el) =>
												![
													'OTHER',
													'getById',
													'getAll',
												].includes(el[0])
										)
										.map((el) => [el[0], el[0]]),
								].map(([key, value]) => (
									<MenuItem key={key} value={key}>{value}</MenuItem>
								))}
							</Select>
						</FormControl>

						<DatePicker
							label="Start Date"
							format="dd/MM/yyyy"
							value={filter.from}
							onChange={(e) => onChangeFilter('from', e)}
							inputVariant="outlined"
							renderInput={(params) => (
								<Box sx={{ minWidth: { xs: '100%', md: 200 } }}>
									{params.input}
								</Box>
							)}
						/>

						<DatePicker
							label="End Date"
							format="dd/MM/yyyy"
							value={filter.to}
							onChange={(e) => onChangeFilter('to', e)}
							inputVariant="outlined"
							minDate={filter.from}
							renderInput={(params) => (
								<Box sx={{ minWidth: { xs: '100%', md: 200 } }}>
									{params.input}
								</Box>
							)}
						/>

						<Button
							variant="contained"
							color="primary"
							size="large"
							startIcon={<SearchIcon />}
							sx={{
								borderRadius: '12px',
								px: 3,
								py: 1.5,
								background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
								boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
								'&:hover': {
									background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
									boxShadow: '0 6px 24px rgba(25, 118, 210, 0.4)',
									transform: 'translateY(-2px)'
								}
							}}
						>
							Search
						</Button>

						<Button
							variant="contained"
							color="secondary"
							size="large"
							startIcon={<DownloadForOffline />}
							onClick={() =>
								downloadReport({
									rows,
									columns,
									fileName: `Sadhna Report ${USER_ROLE[filter.category]
										} ${moment(new Date(filter.from)).format(
											'Do MMMM yy'
										)}-${moment(new Date(filter.to)).format(
											'Do MMMM yy'
										)}.xlsx`,
								})
							}
							sx={{
								borderRadius: '12px',
								px: 3,
								py: 1.5,
								boxShadow: '0 4px 16px rgba(156, 39, 176, 0.3)',
							}}
						>
							Download Report
						</Button>
					</Box>
				</CardContent>
			</Card>

			{/* Stats Cards */}
			<Grid2 container spacing={3} sx={{ mb: 3 }}>
				<Grid2 xs={12} sm={6} md={3}>
					<Card sx={{
						background: 'rgba(255, 255, 255, 0.95)',
						backdropFilter: 'blur(10px)',
						borderRadius: '16px',
						boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
						textAlign: 'center',
						p: 2
					}}>
						<PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
						<Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
							{users.length}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Total Users
						</Typography>
					</Card>
				</Grid2>
				<Grid2 xs={12} sm={6} md={3}>
					<Card sx={{
						background: 'rgba(255, 255, 255, 0.95)',
						backdropFilter: 'blur(10px)',
						borderRadius: '16px',
						boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
						textAlign: 'center',
						p: 2
					}}>
						<AssessmentIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
						<Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
							{rows.length}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Reports Generated
						</Typography>
					</Card>
				</Grid2>
				<Grid2 xs={12} sm={6} md={3}>
					<Card sx={{
						background: 'rgba(255, 255, 255, 0.95)',
						backdropFilter: 'blur(10px)',
						borderRadius: '16px',
						boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
						textAlign: 'center',
						p: 2
					}}>
						<CalendarIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
						<Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
							{moment(filter.to).diff(moment(filter.from), 'days')}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Days Covered
						</Typography>
					</Card>
				</Grid2>
				<Grid2 xs={12} sm={6} md={3}>
					<Card sx={{
						background: 'rgba(255, 255, 255, 0.95)',
						backdropFilter: 'blur(10px)',
						borderRadius: '16px',
						boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
						textAlign: 'center',
						p: 2
					}}>
						<FilterIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
						<Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
							{filter.category}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Category Selected
						</Typography>
					</Card>
				</Grid2>
			</Grid2>

			{/* Data Grid Section */}
			<Card sx={{
				background: 'rgba(255, 255, 255, 0.95)',
				backdropFilter: 'blur(10px)',
				borderRadius: '20px',
				boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
				overflow: 'hidden'
			}}>
				<CardContent sx={{ p: 0 }}>
					{/* Loading State */}
					{isLoading && (
						<Box sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							py: 8
						}}>
							<CircularProgress size={60} />
						</Box>
					)}

					{/* Data Grid */}
					{!isLoading && (
						<Box sx={{ p: 3 }}>
							<Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
								Report Results
							</Typography>
							<CustomizedDataGrid
								rows={rows}
								columns={columns}
								onRowClick={onRowClick}
								loading={isLoading}
							/>
						</Box>
					)}

					{/* Empty State */}
					{!isLoading && rows.length === 0 && (
						<Box sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							py: 8,
							px: 3
						}}>
							<AssessmentIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
							<Typography variant="h6" color="text.secondary" gutterBottom>
								No Data Available
							</Typography>
							<Typography variant="body2" color="text.disabled" textAlign="center">
								Try adjusting your filters or date range to see results
							</Typography>
						</Box>
					)}
				</CardContent>
			</Card>
		</Box>
	)
}

export default SadhnaReportContainer
